/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { Checkbox, Tag } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import Text from '../../ts-components/Text'
import { ErrorLabel } from '../../ts-components/ErrorLabel'
import { paragraphSmall } from 'src/components/css'
import {
  STANDALONE_ROLES_TOP,
  STANDALONE_ROLES_BOTTOM,
  STANDALONE_ROLES,
  ROLE_GROUPS,
  buildDisplayTags,
  ALL_ROLES,
  EVERYONE_COLORS,
} from '@sportninja/common/constants/roleGroups'
import type {
  DisplayTag,
  RoleGroup,
} from '@sportninja/common/constants/roleGroups'

const PLURAL_MAP: Record<string, string> = {
  Player: 'Players',
  'Team Official': 'Team Officials',
  Videographer: 'Videographers',
  Owner: 'Owners',
  Admin: 'Admins',
  'League Manager': 'League Managers',
  Referee: 'Referees',
  Scorekeeper: 'Scorekeepers',
  Timekeeper: 'Timekeepers',
}

const pluralize = (name: string): string => PLURAL_MAP[name] || name

// Re-export for consumers that import from this file
export {
  STANDALONE_ROLES_TOP,
  STANDALONE_ROLES_BOTTOM,
  STANDALONE_ROLES,
  ROLE_GROUPS,
}

interface GroupedRoleSelectorProps {
  value: string[]
  onChange: (value: string[]) => void
  fieldErrors: string[]
  customError: { [key: string]: string }
  name: string
}

const GroupedRoleSelector: React.FC<GroupedRoleSelectorProps> = ({
  value,
  onChange,
  fieldErrors,
  customError,
  name,
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handler)
    }
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const isRoleSelected = useCallback(
    (roleId: string) => value.includes(roleId),
    [value]
  )

  const getGroupState = useCallback(
    (group: RoleGroup) => {
      const selectedCount = group.roles.filter((r) =>
        value.includes(r.id)
      ).length
      return {
        checked: selectedCount === group.roles.length,
        indeterminate: selectedCount > 0 && selectedCount < group.roles.length,
      }
    },
    [value]
  )

  const handleGroupToggle = useCallback(
    (group: RoleGroup) => {
      const groupIds = group.roles.map((r) => r.id)
      const allSelected = groupIds.every((id) => value.includes(id))

      if (allSelected) {
        onChange(value.filter((id) => !groupIds.includes(id)))
      } else {
        onChange([...new Set([...value, ...groupIds])])
      }
    },
    [value, onChange]
  )

  const allRoleIds = useMemo(() => Object.keys(ALL_ROLES), [])

  const isAllSelected = useMemo(
    () => allRoleIds.length > 0 && allRoleIds.every((id) => value.includes(id)),
    [allRoleIds, value]
  )

  const handleEveryoneToggle = useCallback(() => {
    if (isAllSelected) {
      onChange([])
    } else {
      onChange([...allRoleIds])
    }
  }, [isAllSelected, allRoleIds, onChange])

  const handleRoleToggle = useCallback(
    (roleId: string) => {
      if (value.includes(roleId)) {
        onChange(value.filter((id) => id !== roleId))
      } else {
        onChange([...value, roleId])
      }
    },
    [value, onChange]
  )

  const hasError = useMemo(
    () => fieldErrors?.includes(name),
    [fieldErrors, name]
  )

  const displayTags: DisplayTag[] = useMemo(
    () => buildDisplayTags(value),
    [value]
  )

  const handleRemoveTag = useCallback(
    (roleIds: string[], e: React.MouseEvent) => {
      e.stopPropagation()
      onChange(value.filter((id) => !roleIds.includes(id)))
    },
    [value, onChange]
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 100%;
      `}
    >
      <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
        Target Audience *
      </Text>
      <div
        ref={ref}
        css={css`
          position: relative;
        `}
      >
        {/* Select-like trigger */}
        <div
          css={css`
            min-height: 38px;
            padding: 4px 8px;
            background-color: ${colors.SECONDARY_200};
            border-radius: 8px;
            border: 1px solid ${hasError ? colors.ERROR_300 : 'transparent'};
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 4px;
            cursor: pointer;

            &:hover {
              border-color: ${hasError
                ? colors.ERROR_300
                : colors.SECONDARY_75};
            }
          `}
          onClick={() => setOpen((v) => !v)}
        >
          {displayTags.length === 0 ? (
            <span
              css={css`
                ${paragraphSmall}
                color: ${colors.NEUTRAL_100};
              `}
            >
              Admins, Officials, Players...
            </span>
          ) : (
            displayTags.map((tag) => (
              <Tag
                key={tag.key}
                closable
                onClose={(e) => handleRemoveTag(tag.roleIds, e as any)}
                closeIcon={
                  <CloseOutlined
                    css={css`
                      color: ${tag.colors.fg} !important;
                      font-size: 10px;
                    `}
                  />
                }
                css={css`
                  background-color: ${tag.colors.bg} !important;
                  border: none;
                  margin: 0;
                  padding: 2px 8px;
                  .ant-tag-close-icon {
                    margin-inline-start: 4px;
                  }
                `}
              >
                <span
                  css={css`
                    ${paragraphSmall}
                    color: ${tag.colors.fg};
                  `}
                >
                  {pluralize(tag.label)}
                </span>
              </Tag>
            ))
          )}
        </div>

        {/* Dropdown panel */}
        {open && (
          <div
            css={css`
              position: absolute;
              z-index: 10002;
              top: calc(100% + 4px);
              left: 0;
              right: 0;
              background: ${colors.SECONDARY_300};
              border: 1px solid ${colors.SECONDARY_100};
              border-radius: 8px;
              padding: 8px 12px;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
              max-height: min-content;
              overflow-y: visible;
            `}
          >
            {/* Everyone option */}
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 6px 0;
                cursor: pointer;
                &:hover {
                  opacity: 0.85;
                }
              `}
              onClick={handleEveryoneToggle}
            >
              <Checkbox
                checked={isAllSelected}
                onChange={handleEveryoneToggle}
              />
              <span
                css={css`
                  background: ${EVERYONE_COLORS.bg};
                  color: ${EVERYONE_COLORS.fg};
                  padding: 1px 8px;
                  border-radius: 4px;
                  font-size: 13px;
                  font-weight: 600;
                  line-height: 20px;
                `}
              >
                Everyone
              </span>
            </div>

            {/* All roles indented under Everyone */}
            <div
              css={css`
                padding-left: 24px;
              `}
            >
              {/* Standalone roles at top */}
              {STANDALONE_ROLES_TOP.map((role) => (
                <div
                  key={role.id}
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 6px 0;
                    cursor: pointer;
                    &:hover {
                      opacity: 0.85;
                    }
                  `}
                  onClick={() => handleRoleToggle(role.id)}
                >
                  <Checkbox
                    checked={isRoleSelected(role.id)}
                    onChange={() => handleRoleToggle(role.id)}
                  />
                  <span
                    css={css`
                      background: ${role.colors.bg};
                      color: ${role.colors.fg};
                      padding: 1px 8px;
                      border-radius: 4px;
                      font-size: 13px;
                      font-weight: 600;
                      line-height: 20px;
                    `}
                  >
                    {pluralize(role.name)}
                  </span>
                </div>
              ))}

              {ROLE_GROUPS.map((group) => {
                const { checked, indeterminate } = getGroupState(group)
                return (
                  <div key={group.label}>
                    {/* Group header */}
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        padding: 6px 0;
                        cursor: pointer;
                        &:hover {
                          opacity: 0.85;
                        }
                      `}
                      onClick={() => handleGroupToggle(group)}
                    >
                      <Checkbox
                        checked={checked}
                        indeterminate={indeterminate}
                        onChange={() => handleGroupToggle(group)}
                      />
                      <span
                        css={css`
                          background: ${group.colors.bg};
                          color: ${group.colors.fg};
                          padding: 1px 8px;
                          border-radius: 4px;
                          font-size: 13px;
                          font-weight: 600;
                          line-height: 20px;
                        `}
                      >
                        {group.label}
                      </span>
                    </div>

                    {/* Individual roles */}
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        padding-left: 24px;
                        gap: 2px;
                      `}
                    >
                      {group.roles.map((role) => (
                        <div
                          key={role.id}
                          css={css`
                            display: flex;
                            align-items: center;
                            gap: 8px;
                            padding: 3px 0;
                            cursor: pointer;
                            &:hover {
                              opacity: 0.85;
                            }
                          `}
                          onClick={() => handleRoleToggle(role.id)}
                        >
                          <Checkbox
                            checked={isRoleSelected(role.id)}
                            onChange={() => handleRoleToggle(role.id)}
                          />
                          <Text
                            variant='paragraphSmall'
                            weight='regular'
                            color={colors.NEUTRAL_50}
                          >
                            {pluralize(role.name)}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}

              {/* Standalone roles at bottom */}
              {STANDALONE_ROLES_BOTTOM.map((role) => (
                <div
                  key={role.id}
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 6px 0;
                    cursor: pointer;
                    &:hover {
                      opacity: 0.85;
                    }
                  `}
                  onClick={() => handleRoleToggle(role.id)}
                >
                  <Checkbox
                    checked={isRoleSelected(role.id)}
                    onChange={() => handleRoleToggle(role.id)}
                  />
                  <span
                    css={css`
                      background: ${role.colors.bg};
                      color: ${role.colors.fg};
                      padding: 1px 8px;
                      border-radius: 4px;
                      font-size: 13px;
                      font-weight: 600;
                      line-height: 20px;
                    `}
                  >
                    {pluralize(role.name)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <ErrorLabel
        fieldErrors={fieldErrors || []}
        customError={customError || ''}
        name={name}
      />
    </div>
  )
}

export default GroupedRoleSelector
